import { useEffect, useMemo } from "react";
import { useElementSize, useMediaQuery, useShortcuts, useStationEvents } from "./game/hooks";
import { getEngine, useStation } from "./game/runtime";
import { session, useSession } from "./game/session";
import { useGameUi } from "./game/store";
import { shouldAutoStart, useTutorial } from "./game/tutorial";
import { StationCanvas } from "./render/StationCanvas";
import { Hud } from "./ui/Hud";
import { BootScreen, SandboxControls } from "./ui/Overlays";
import { LevelUpDialog } from "./ui/Progression";
import { BlockedDialog, LoginLinkDialog, OfflineReportDialog, StatusBar, TakeoverDialog } from "./ui/SessionScreens";
import { BuildBanner, Deck, PanelFrame, Toasts } from "./ui/Shell";
import { SignInScreen } from "./ui/SignInScreen";
import { Tutorial } from "./ui/Tutorial";
import { AccountSection } from "./ui/panels/AccountSection";
import { BuildPanel } from "./ui/panels/BuildPanel";
import { FailuresPanel } from "./ui/panels/FailuresPanel";
import { ModulePanel } from "./ui/panels/ModulePanel";
import { ShipPanel, ShipsPanel } from "./ui/panels/ShipsPanel";
import { StationPanel } from "./ui/panels/StationPanel";

/** Un compte est obligatoire : sans lui, l'écran d'accueil ; avec lui, la station. */
export function App() {
  const phase = useSession((state) => state.phase.kind);
  return phase === "signin" ? <SignInScreen /> : <Game />;
}

function Game() {
  const state = useStation();
  const phase = useSession((session) => session.phase);
  const offlineReport = useSession((session) => session.offlineReport);
  const loginLink = useSession((session) => session.loginLink);
  const playerEmail = useSession((session) => session.player?.email ?? null);
  const panel = useGameUi((ui) => ui.panel);
  const selection = useGameUi((ui) => ui.selection);
  const selectedShip = useGameUi((ui) => ui.selectedShip);
  const openPanel = useGameUi((ui) => ui.openPanel);
  const selectShip = useGameUi((ui) => ui.selectShip);
  const levelUp = useGameUi((ui) => ui.levelUp);
  const tutorialActive = useTutorial((tutorial) => tutorial.active);
  const desktop = useMediaQuery("(min-width: 768px)");
  const [measureHud, hud] = useElementSize();
  const [measureLeft, left] = useElementSize();
  const [measureDock, dock] = useElementSize();
  useStationEvents();
  useShortcuts();
  const sandbox = phase.kind === "sandbox";
  const playing = phase.kind === "playing" || sandbox;

  // Le tutoriel de l'arrivée : station toute neuve, jamais vu sur cet appareil.
  const hasState = state !== null;
  useEffect(() => {
    const tutorial = useTutorial.getState();
    const station = getEngine()?.state;
    if (!hasState || !playing || tutorial.active || !station) return;
    if (shouldAutoStart(station)) tutorial.start();
  }, [hasState, playing]);

  // Lien de connexion du compte déjà connecté ici : rien à faire.
  useEffect(() => {
    if (loginLink && playerEmail && loginLink.email === playerEmail) session.consumeLoginLink();
  }, [loginLink, playerEmail]);

  const selected = selection ? state?.modules.find((module) => module.id === selection) : undefined;
  const ship = selectedShip ? state?.ships.find((candidate) => candidate.id === selectedShip) : undefined;

  // Un vaisseau parti ferme sa fiche.
  useEffect(() => {
    if (selectedShip && state && !ship) selectShip(null);
  }, [selectedShip, state, ship, selectShip]);

  const insets = useMemo(
    () =>
      desktop
        ? { top: hud.height + 16, left: left.width + 28, right: 16, bottom: 16 }
        : { top: hud.height + 8, left: 8, right: 8, bottom: dock.height + 8 },
    [desktop, hud.height, left.width, dock.height],
  );

  // Une seule fenêtre à la fois, de la plus bloquante à la plus anodine.
  const dialog =
    phase.kind === "blocked" ? (
      <BlockedDialog reason={phase.reason} />
    ) : phase.kind === "takenOver" ? (
      <TakeoverDialog by={phase.by} />
    ) : offlineReport && state && phase.kind === "playing" ? (
      <OfflineReportDialog report={offlineReport} state={state} />
    ) : loginLink && playerEmail && loginLink.email !== playerEmail && phase.kind === "playing" ? (
      <LoginLinkDialog link={loginLink} />
    ) : levelUp !== null && playing ? (
      <LevelUpDialog level={levelUp} />
    ) : null;

  if (!state) {
    return (
      <>
        <BootScreen />
        {dialog}
      </>
    );
  }

  const content = selected ? (
    <ModulePanel key={selected.id} state={state} module={selected} />
  ) : ship ? (
    <ShipPanel key={ship.id} state={state} ship={ship} />
  ) : panel === "build" ? (
    <BuildPanel state={state} onPick={desktop ? undefined : () => openPanel(null)} />
  ) : panel === "failures" ? (
    <FailuresPanel state={state} />
  ) : panel === "ships" ? (
    <ShipsPanel state={state} />
  ) : panel === "station" ? (
    <StationPanel state={state}>{sandbox ? null : <AccountSection />}</StationPanel>
  ) : null;

  return (
    <div className="relative h-full w-full overflow-hidden">
      <StationCanvas insets={insets} />

      <div ref={measureHud} className="pointer-events-none absolute inset-x-0 top-0 z-20">
        <Hud state={state} desktop={desktop} />
      </div>
      {/* Bandeaux centrés sur la partie libre de l'écran, à côté du pupitre et du panneau. */}
      <div
        className="pointer-events-none absolute inset-x-0 z-20"
        style={{ top: hud.height, paddingLeft: desktop ? insets.left - 16 : 0 }}
      >
        <StatusBar />
        <BuildBanner state={state} />
      </div>

      {desktop ? (
        <>
          <div
            ref={measureLeft}
            className="pointer-events-none absolute bottom-3 left-3 z-10 flex items-start gap-[var(--px)]"
            style={{ top: hud.height + 8 }}
          >
            <Deck state={state} vertical />
            {content ? <PanelFrame desktop>{content}</PanelFrame> : null}
          </div>
          {sandbox ? (
            <div className="pointer-events-none absolute bottom-3 right-3 z-10">
              <SandboxControls />
            </div>
          ) : null}
          {/* Les messages restent dans la partie libre, à gauche du bandeau du tutoriel s'il est là. */}
          <div
            className="pointer-events-none absolute inset-x-0 bottom-6 z-30"
            style={{ paddingLeft: insets.left, paddingRight: tutorialActive ? "28rem" : 0 }}
          >
            <Toasts />
          </div>
        </>
      ) : (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex flex-col">
          <div className="mb-2 px-3">
            <Toasts />
          </div>
          {sandbox && !content ? (
            <div className="mb-2 flex justify-end px-3">
              <SandboxControls />
            </div>
          ) : null}
          <div ref={measureDock} className="flex flex-col">
            {content ? <PanelFrame desktop={false}>{content}</PanelFrame> : null}
            <Deck state={state} vertical={false} />
          </div>
        </div>
      )}

      {tutorialActive && !dialog ? <Tutorial state={state} desktop={desktop} bottom={dock.height + 8} /> : null}
      {dialog}
    </div>
  );
}
